import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { CompactionHistoryEvent, CompactionHistoryRow, HistoryField } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { FormService } from 'src/app/shared/services/form.service';
import { plural } from 'src/app/shared/components/history-timeline/history-timeline.component';
import { HistoryChange } from 'src/app/shared/models/history-timeline.modal';

/**
 * Timeline of changes made from the Compaction worksheet (Save Table, the Completed checkbox and assignments).
 * Each entry shows the whole worksheet as it stood after the action; the rows it changed are highlighted, with the
 * previous value of each changed field.
 * Changes made to the same rows from other screens (TR worksheet, TPA) are not part of this history.
 */
@Component({
    selector: 'app-compaction-history',
    templateUrl: './compaction-history.component.html',
    styleUrls: ['./compaction-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompactionHistoryComponent implements OnInit {
  projectId: string | null = null;
  companyName = '';
  projectName = '';
  referenceId = '';
  // Newest first: the most recent action is the leftmost point on the timeline
  events: CompactionHistoryEvent[] = [];
  selectedIndex = -1;
  isLoading = true;
  // Same order as the Compaction worksheet table
  displayedColumns = [
    'sampleId', 'effort', 'depth', 'inHouseId', 'mouldPlusProbeTare', 'soilDiameter', 'tubeHeight', 'minus',
    'weight', 'tr', 'geothermSoilDescription', 'isCompactionCompleted', 'assignedTo',
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private worksheetService: WorksheetService,
    private formService: FormService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    if (!this.projectId) {
      this.isLoading = false;
      return;
    }

    this.formService.fetchFormData(this.projectId, 'nativesoil').subscribe({
      next: (result) => {
        this.companyName = result?.company?.name ?? '';
        this.projectName = result?.project?.projectName ?? '';
        this.referenceId = result?.project?.referenceId ?? '';
      },
    });

    this.worksheetService.getCompactionHistory(this.projectId).subscribe({
      next: (events) => {
        // The API returns newest first, which is also the timeline's left-to-right order
        this.events = (Array.isArray(events) ? events : []).map((event) => ({ ...event, changes: this.buildChanges(event) }));
        this.isLoading = false;
        // Open on the latest action (the leftmost point)
        this.selectedIndex = this.events.length > 0 ? 0 : -1;
      },
      error: () => {
        this.events = [];
        this.isLoading = false;
      },
    });
  }

  get selectedEvent(): CompactionHistoryEvent | null {
    return this.events[this.selectedIndex] ?? null;
  }

  backToWorksheet(): void {
    this.router.navigate(['compaction-worksheet'], { queryParams: { projectId: this.projectId } });
  }

  reDirectUrl(): void {
    this.router.navigate(['dashboard']);
  }

  // Effort 0 is a tube sample
  displayEffort(row: CompactionHistoryRow): string {
    if (row.effort === null || row.effort === undefined) {
      return '—';
    }
    return row.effort === 0 ? 'Tube' : `${row.effort}`;
  }

  displayDepth(row: CompactionHistoryRow): string {
    if (row.depth === null || row.depth === undefined) {
      return '';
    }
    return row.endDepth !== null && row.endDepth !== undefined ? `${row.depth}-${row.endDepth}` : `${row.depth}`;
  }

  displayValue(value: string | number | boolean | null | undefined, isStatus = false): string {
    if (isStatus) {
      return value ? 'Completed' : 'Not completed';
    }
    return value === null || value === undefined || value === '' ? '—' : `${value}`;
  }

  // Fields counted as edits (assignment is counted separately)
  private valueFields(row: CompactionHistoryRow): HistoryField<unknown>[] {
    return [row.inHouseId, row.mouldPlusProbeTare, row.soilDiameter, row.tubeHeight, row.minus, row.weight, row.tr, row.geothermSoilDescription];
  }

  // Timeline icons: edits (values and completed status) and assignments, each with its count
  private buildChanges(event: CompactionHistoryEvent): HistoryChange[] {
    const editedValues = event.rows.reduce((total, row) => total + this.valueFields(row).filter((field) => field.changed).length, 0);
    const completed = event.rows.filter((row) => row.isCompactionCompleted.changed && row.isCompactionCompleted.current).length;
    const uncompleted = event.rows.filter((row) => row.isCompactionCompleted.changed && !row.isCompactionCompleted.current).length;
    const assigned = event.rows.filter((row) => row.assignedTo.changed).length;

    const changes: HistoryChange[] = [];
    const edits = [
      editedValues > 0 ? `${plural(editedValues, 'value')} edited` : '',
      completed > 0 ? `${plural(completed, 'row')} marked completed` : '',
      uncompleted > 0 ? `${plural(uncompleted, 'row')} marked not completed` : '',
    ].filter(Boolean);
    if (edits.length > 0) {
      changes.push({ kind: 'edited', tooltip: edits.join(', ') });
    }
    if (assigned > 0) {
      changes.push({ kind: 'assigned', tooltip: `${plural(assigned, 'row')} assigned` });
    }
    return changes;
  }

  // Same group lines as the worksheet: dotted between efforts of a sample, thick under the last row of a sample
  isLastRowOfEffort(rows: CompactionHistoryRow[], index: number): boolean {
    const nextRow = rows[index + 1];
    return !!nextRow && nextRow.sampleId === rows[index].sampleId && nextRow.effort !== rows[index].effort;
  }

  isLastRowOfSample(rows: CompactionHistoryRow[], index: number): boolean {
    const nextRow = rows[index + 1];
    return !!nextRow && nextRow.sampleId !== rows[index].sampleId;
  }

  changedRowCount(event: CompactionHistoryEvent): number {
    return event.rows.filter((row) => row.changedInAction).length;
  }

  changedFieldCount(event: CompactionHistoryEvent): number {
    return event.rows.reduce(
      (total, row) =>
        total +
        this.valueFields(row).filter((field) => field.changed).length +
        (row.isCompactionCompleted.changed ? 1 : 0) +
        (row.assignedTo.changed ? 1 : 0),
      0,
    );
  }
}
